import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Container,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { organizationService } from '../services/api';
import { Organization } from '../types';

const OrganizationSelection: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, logout, isLoading: authLoading, setUserOrganization } = useAuth();
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  const handleOrganizationSelect = (organization: Organization) => {
    // Set the organization in the user context
    setUserOrganization(organization.org_id, organization.org_name);
    // Navigate to the map page with the selected organization
    navigate(`/organization/${organization.org_id}/map`);
  };

  // Redirect to login if user is not authenticated
  useEffect(() => {
    if (!user && !authLoading) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    // Prevent fetching if we've already fetched or if we're redirecting
    if (hasFetched.current || isRedirecting || authLoading) {
      return;
    }

    const fetchOrganizations = async () => {
      if (!user?.id) {
        setError('User not authenticated');
        setIsLoading(false);
        // Redirect to login if user is not authenticated
        navigate('/login');
        return;
      }

      // Mark as fetched to prevent duplicate calls
      hasFetched.current = true;

      try {
        setIsLoading(true);
        setError(null);
        
        const response = await organizationService.getAllOrganizationsByUserId(user.id);
        
        if (response.success && response.data) {
          setOrganizations(response.data);
          
          // If there's only one organization, auto-select it and redirect to Map page
          if (response.data.length === 1) {
            setIsRedirecting(true);
            // Set the organization in context and navigate immediately
            setUserOrganization(response.data[0].org_id, response.data[0].org_name);
            navigate(`/organization/${response.data[0].org_id}/map`);
          }
        } else {
          setError(response.message || 'Failed to fetch organizations');
        }
      } catch (err) {
        let errorMessage = 'An unexpected error occurred';
        
        if (err instanceof Error) {
          if (err.message.includes('Failed to fetch') || err.message.includes('Network error')) {
            errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
          } else if (err.message.includes('CORS')) {
            errorMessage = 'Server connection error. Please contact support if this issue persists.';
          } else {
            errorMessage = err.message;
          }
        }
        
        setError(errorMessage);
        // Reset fetch flag on error so user can retry
        hasFetched.current = false;
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, navigate, authLoading]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (authLoading || isLoading || isRedirecting) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: (theme) => theme.palette.primary.main,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={24}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              p: 4,
              textAlign: 'center',
            }}
          >
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {isRedirecting ? 'Redirecting to your organization...' : 'Loading organizations...'}
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: (theme) => theme.palette.primary.main,
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              background: 'white',
              color: '000000',
              textAlign: 'center',
              py: 4,
              px: 3,
            }}
          >
            <img 
              src={process.env.PUBLIC_URL + '/Aneo-logo-primary-black_RGB.svg'} 
              alt="Aneo Logo" 
              style={{ 
                width: '80px', 
                height: '80px'
              }} 
            />
            <Typography variant="h2" component="h1" gutterBottom>
              Select Organization
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Choose which organization you want to access
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {organizations.length === 0 && !error && (
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                No organizations found for your account.
              </Alert>
            )}

            {organizations.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  {organizations.length === 1 ? 'Your Organization' : 'Available Organizations'}
                </Typography>
                {organizations.length === 1 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                    Redirecting to your organization...
                  </Typography>
                )}
                <List sx={{ mb: 3 }}>
                  {organizations.map((org, index) => (
                    <React.Fragment key={org.org_id}>
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => handleOrganizationSelect(org)}
                          sx={{
                            borderRadius: 2,
                            mb: 1,
                            mt: 1,
                            '&:hover': {
                              backgroundColor: (theme) => theme.palette.action.hover,
                            },
                          }}
                        >
                          <ListItemText
                            primary={org.org_name}
                            secondary={`Organization ID: ${org.org_id}`}
                            primaryTypographyProps={{
                              variant: 'h6',
                              fontWeight: 'medium',
                            }}
                            secondaryTypographyProps={{
                              variant: 'body2',
                              color: 'text.secondary',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                      {index < organizations.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </>
            )}

            <Button
              fullWidth
              variant="outlined"
              onClick={handleLogout}
              sx={{
                py: 1.5,
                borderRadius: 2,
                borderColor: (theme) => theme.palette.error.main,
                color: (theme) => theme.palette.error.main,
                '&:hover': {
                  borderColor: (theme) => theme.palette.error.dark,
                  backgroundColor: (theme) => theme.palette.error.light + '20',
                },
              }}
            >
              Sign Out
            </Button>
          </CardContent>
        </Paper>
      </Container>
    </Box>
  );
};

export default OrganizationSelection;
